<template>
  <l-form
    ref="formRef"
    class="flex flex-col gap-4"
    @submit="handleSubmit"
    :validation-schema="formSchema"
  >
    <div class="body-1">{{ t('reservations.personal-information') }}</div>

    <l-form-field name="phoneNumber" v-slot="{ field }">
      <l-form-label :label="t('reservations.phone-number')" />
      <l-phone-input
        v-bind="field"
        icon="phone"
        :placeholder="t('reservations.phone-number')"
        :default-country-code="config.locationCountryCode"
        :loading="searchingCustomer"
        @update:model-value="onPhoneNumberChange"
      />
      <l-form-error />
    </l-form-field>

    <div v-if="searchingCustomer" class="flex flex-col items-center gap-2 py-4">
      <l-icon name="status" size="small" class="animate-spin" />
      <div class="body-2 text-n-300">
        {{ t('reservations.searching-customer') }}
      </div>
    </div>

    <template v-if="showFullForm">
      <div class="flex items-start justify-between gap-4">
        <l-form-field name="name" class="w-full" v-slot="{ field }">
          <l-form-label :label="t('reservations.name')" />
          <l-input v-bind="field" :placeholder="t('reservations.name')" />
          <l-form-error />
        </l-form-field>
        <l-form-field name="surname" class="w-full" v-slot="{ field }">
          <l-form-label :label="t('reservations.surname')" />
          <l-input v-bind="field" :placeholder="t('reservations.surname')" />
          <l-form-error />
        </l-form-field>
      </div>

      <l-form-field name="email" class="w-full" v-slot="{ field }">
        <l-form-label :label="t('reservations.email')" />
        <l-input v-bind="field" :placeholder="t('reservations.email')" />
        <l-form-error />
      </l-form-field>

      <l-form-field name="customerComments" v-slot="{ field }">
        <l-form-label :label="t('reservations.comments')" />
        <l-textarea v-bind="field" :placeholder="t('reservations.comments-placeholder')" />
        <l-form-error />
      </l-form-field>
    </template>
  </l-form>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import parsePhoneNumber from 'libphonenumber-js'
import { computed, onMounted, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { z } from 'zod'

import {
  LForm,
  LFormError,
  LFormField,
  LFormLabel,
  LIcon,
  LInput,
  LPhoneInput,
  LTextarea
} from '@last/core-ui/paprika'

import { getCustomer } from '@/api/customers'
import { useConfigStore } from '@/store/config'

import { zodErrorMessages } from '../zod-error-messages'

z.setErrorMap(zodErrorMessages)

interface FormValues {
  phoneNumber: string
  name: string
  surname?: string | null
  customerComments?: string | null
  email?: string | null
}

const props = defineProps<{
  data?: {
    name: string
    surname: string | null
    phoneNumber: string
    customerComments: string | null
    email: string | null
  }
}>()

const emit = defineEmits<{
  submit: [values: FormValues]
}>()

const { t } = useI18n()

const { config } = useConfigStore()
const formRef = useTemplateRef('formRef')

const searchingCustomer = ref(false)
const hasSearchedPhone = ref(false)

const schema = z.object({
  phoneNumber: z.string().refine(
    value => {
      return !!parsePhoneNumber(value)?.isValid()
    },
    {
      message: t('forms.phone-number-invalid')
    }
  ),
  name: z.string().trim().nonempty(),
  surname: z.string().nullish(),
  customerComments: z.string().nullish(),
  email: z
    .string()
    .nullish()
    .refine(value => !value || z.string().email().safeParse(value).success, {
      message: t('forms.email-invalid')
    })
})

const formSchema = toTypedSchema(schema)

const showFullForm = computed(() => {
  return !!props.data || hasSearchedPhone.value
})

async function onPhoneNumberChange(phoneNumber: string) {
  if (!phoneNumber) {
    hasSearchedPhone.value = false
    return
  }

  const parsedPhone = parsePhoneNumber(phoneNumber)
  if (!parsedPhone?.isValid()) {
    return
  }

  if (props.data) {
    return
  }

  try {
    searchingCustomer.value = true
    const customer = await getCustomer(phoneNumber)
    hasSearchedPhone.value = true

    formRef.value?.setValues({
      phoneNumber: phoneNumber,
      name: customer.customer.name,
      surname: customer.customer.surname,
      customerComments: customer.customer.internalNote,
      email: customer.customer.email
    })
  } catch {
    hasSearchedPhone.value = true
    formRef.value?.setValues(
      {
        phoneNumber: phoneNumber,
        name: undefined,
        surname: undefined,
        customerComments: undefined,
        email: undefined
      },
      false
    )
  } finally {
    searchingCustomer.value = false
  }
}

function handleSubmit(values: FormValues) {
  emit('submit', values)
}

onMounted(() => {
  if (props.data) {
    formRef.value?.setValues({
      name: props.data.name,
      surname: props.data.surname,
      phoneNumber: props.data.phoneNumber,
      customerComments: props.data.customerComments,
      email: props.data.email
    })
  }
})

defineExpose({
  formRef,
  requestSubmit: () => formRef.value?.requestSubmit()
})
</script>
